import React, { useEffect, useMemo, useState } from 'react'
import { LinearProgress, styled } from '@mui/material'
import { DataGrid, GridRow, GridColumnHeaders, ptBR } from '@mui/x-data-grid'
import { useConversionReport } from 'hooks/useConversionReport'
import { CustomGridPagination, CustomNoRowsOverlay } from './Customized'

const MemoizedRow = React.memo(GridRow)
const MemoizedColumnHeaders = React.memo(GridColumnHeaders)

const CustomDataGrid = styled(DataGrid)({
  border: 'none',
  '.grid-list-virtual-scroller': {
    minHeight: 'fit-content',
  },
  '&.grid-list-root .grid-list-cell:focus-within': {
    outline: 'none !important',
  },
  '.grid-list-columnHeader:focus-within': {
    outline: 'none',
  },
  '.grid-list-columnHeader': {
    cursor: 'pointer',
  },
  '.grid-list-columnHeaders': {
    width: '100%',
    borderBottom: '2px solid #0076F1',
  },
  '.grid-list-columnHeaderTitleContainerContent': {
    width: '100%',
    fontWeight: 'bold',
    wrapSpace: 'normal',
  },
  '.HiTheme-DataGrid-overlayWrapperInner, .HiTheme-DataGrid-overlayWrapper': {
    height: '473px',
  },
})

function Grid({ rows, columns, loading, rowCount, pageSizeOptions, slotProps, ...restDataGridProps }) {
  const { state, actions } = useConversionReport()
  const [sortModel, setSortModel] = useState([])

  const { page, search } = state

  const handlePaginationModelChange = (model) => {
    const { page: current, pageSize: size } = model
    actions.setPagination({ current, size })
  }

  const handleModelChange = (model) => {
    setSortModel(model)
    if (model?.length) {
      const order = model[0]?.sort?.toLocaleLowerCase() ?? 'desc'
      actions.setPagination({ order })
    }
  }

  useEffect(() => {
    if (search === 'advertisement') {
      setSortModel([{ field: 'advertisement', sort: 'asc' }])
    } else {
      setSortModel([{ field: 'campaignName', sort: 'asc' }])
    }
  }, [search])

  const dataGrid = {
    editable: false,
    columns,
    rows,
  }

  const columnVisibilityModel = useMemo(() => {
    if (search === 'campaign') {
      return {
        advertisement: false,
        actions: true,
        Status:false,
        CampaignStatus: true
      }
    }
    return {
      advertisement: true,
      actions: true,
      Status: true,
      CampaignStatus: false
    }
  }, [search])

  return (
    <CustomDataGrid
      {...dataGrid}
      paginationModel={{
        page: page.current,
        pageSize: page.size,
      }}
      data-testid="data-grid"
      columnVisibilityModel={columnVisibilityModel}
      sortingOrder={['asc', 'desc']}
      sortModel={sortModel}
      onSortModelChange={handleModelChange}
      onPaginationModelChange={handlePaginationModelChange}
      paginationMode="server"
      rowCount={rowCount}
      pageSizeOptions={pageSizeOptions}
      loading={loading}
      rowHeight={64}
      hideFooterSelectedRowCount
      disableRowSelectionOnClick
      disableColumnMenu
      classes={{
        root: 'grid-list-root',
        cell: 'grid-list-cell',
        columnHeader: 'grid-list-columnHeader',
        columnHeaderTitleContainer: 'grid-list-columnHeaderTitleContainerContent',
        columnHeaders: 'grid-list-columnHeaders',
        virtualScroller: 'grid-list-virtual-scroller',
      }}
      slots={{
        noRowsOverlay: CustomNoRowsOverlay,
        pagination: CustomGridPagination,
        row: MemoizedRow,
        columnHeaders: MemoizedColumnHeaders,
        loadingOverlay: LinearProgress,
      }}
      slotProps={slotProps}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      {...restDataGridProps}
    />
  )
}

export default Grid
